// DynamicFormComponent.jsx - Enhanced with click-to-insert text library
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import {
  Input,
  Select,
  Radio,
  Checkbox,
  InputNumber,
  Space,
  Typography,
  message,
  Divider,
  Col,
  Row,
  Tabs,
  Button
} from 'antd';
import { DragOutlined, PlusOutlined } from '@ant-design/icons';
import ImageDropZone from './ImageDropZone ';
import "./inspection.css"
import toast from "react-hot-toast";

import categoryService from '@/services/category.service';
import SpeechEnabledInput from '@/components/SpeechEnabledInput';
const { Text } = Typography;
const { Option } = Select;

const DynamicFormComponent = ({
  component,
  control,
  stepIndex,
  unitIndex = null,
  currentValue,
  onDataChange,
  onImageChange,
  onRemoveImage,
  onDragOver,
  onDragLeave,
  onDrop,
  dragOverTarget,
  isCommon = false,
  stepname,
  hideLabel = false,
  onComponentUpdate // Add this prop to update component options
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [category, setCategories] = useState(null);
  const [activeFieldRef, setActiveFieldRef] = useState(null); // Track active field for text insertion
  const categorycall = useRef(false);

  useEffect(() => {
    if (categorycall.current) return; // prevent double call in StrictMode
    categorycall.current = true;

    const fetchCategories = async () => {
      try {
        const response = await categoryService.getAllcategory();
        setCategories(response.categories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      }
    };

    fetchCategories();
  }, []);

  const fieldName = isCommon
    ? `steps.${stepIndex}.commonData.${component.name}`
    : `steps.${stepIndex}.units.${unitIndex}.data.${component.name}`;

  const handleChange = (value) => {
    if (isCommon) {
      onDataChange(stepIndex, component.name, value, true);
    } else {
      onDataChange(stepIndex, unitIndex, component.name, value);
    }
  };

  // Generate categories array from API data
  const categories = useMemo(() => {
    if (!category || !Array.isArray(category)) {
      return [{ value: 'all', label: 'All' }];
    }

    const dynamicCategories = category.map(cat => ({
      value: cat.category_key,
      label: cat.category_key.charAt(0).toUpperCase() + cat.category_key.slice(1)
    }));

    return [{ value: 'all', label: 'All' }, ...dynamicCategories];
  }, [category]);

  // Generate textLibraryData from API data
  const textLibraryData = useMemo(() => {
    if (!category || !Array.isArray(category)) {
      return {};
    }

    const data = {};
    category.forEach(cat => {
      data[cat.category_key] = cat.category_items || [];
    });

    return data;
  }, [category]);

  // Filter texts based on category
  const filteredTexts = useMemo(() => {
    if (selectedCategory === 'all') {
      return Object.values(textLibraryData).flat();
    }
    return textLibraryData[selectedCategory] || [];
  }, [selectedCategory, textLibraryData]);

  // Handle click to append text to field values
  const handleTextClick = useCallback((text) => {
    const componentType = component.type;

    // Handle different field types
    if (['radio', 'radiobutton', 'dropdown', 'checkbox'].includes(componentType)) {
      // Add as option
      const currentOptions = component.options || [];

      if (currentOptions.includes(text)) {
        message.info(`Option "${text}" already exists`);
        return;
      }

      const updatedOptions = [...currentOptions, text];

      if (onComponentUpdate) {
        onComponentUpdate(component.name, 'options', updatedOptions, isCommon);
        toast.success(`Added "${text}" to options`);
      }
    } else if (['text', 'textarea', 'textbox', 'character varying'].includes(componentType)) {
      // Append to existing field value
      const existingValue = currentValue || '';
      const newValue = existingValue ? `${existingValue} ${text}` : text;

      // Update using the field reference if available, otherwise use the change handler
      if (activeFieldRef) {
        activeFieldRef.onChange({ target: { value: newValue } });
      }
      handleChange(newValue);
      toast.success(`Appended "${text}" to field`);
    } else if (['number', 'integer', 'numeric'].includes(componentType)) {
      // For numbers, replace (append doesn't make sense for numeric fields)
      const numValue = parseFloat(text);
      if (!isNaN(numValue)) {
        // Check min/max constraints
        if (component.min !== undefined && numValue < component.min) {
          toast.error(`Value ${numValue} is below minimum ${component.min}`);
          return;
        }
        if (component.max !== undefined && numValue > component.max) {
          toast.error(`Value ${numValue} exceeds maximum ${component.max}`);
          return;
        }

        if (activeFieldRef) {
          activeFieldRef.onChange(numValue);
        }
        handleChange(numValue);
        toast.success(`Set value to "${numValue}"`);
      } else {
        toast.error(`"${text}" is not a valid number`);
      }
    }
  }, [activeFieldRef, component, onComponentUpdate, isCommon, handleChange, currentValue]);

  // Handle drag start from text library (keeping drag functionality as backup)
  const handleLibraryDragStart = (e, text) => {
    e.dataTransfer.setData('text/plain', text);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Handle drag and drop for adding options to option-based components
  const handleOptionDrop = useCallback((e, componentType) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const droppedText = e.dataTransfer.getData('text/plain');
    if (!droppedText || !droppedText.trim()) {
      return;
    }

    const newOption = droppedText.trim();
    const currentOptions = component.options || [];

    // Check if option already exists
    if (currentOptions.includes(newOption)) {
      message.info(`Option "${newOption}" already exists`);
      return;
    }

    const updatedOptions = [...currentOptions, newOption];

    // Update the component options via parent callback
    if (onComponentUpdate) {
      onComponentUpdate(component.name, 'options', updatedOptions, isCommon);
      toast.success(`Added "${newOption}" to options`);
    }
  }, [component.options, component.name, isCommon, onComponentUpdate]);

  // Handle drag and drop for setting text/number values
  const handleTextDrop = useCallback((e, field, componentType) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTextDragOver(false);

    const droppedText = e.dataTransfer.getData('text/plain');
    if (!droppedText || !droppedText.trim()) {
      return;
    }

    let value = droppedText.trim();

    // For number inputs, try to convert to number
    if (['number', 'integer', 'numeric'].includes(componentType)) {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        value = numValue;
        // Check min/max constraints
        if (component.min !== undefined && numValue < component.min) {
          toast.error(`Value ${numValue} is below minimum ${component.min}`);
          return;
        }
        if (component.max !== undefined && numValue > component.max) {
          toast.error(`Value ${numValue} exceeds maximum ${component.max}`);
          return;
        }
      } else {
        toast.error(`"${value}" is not a valid number`);
        return;
      }
    }

    // Update field value
    field.onChange(componentType === 'textarea' ? { target: { value } } : value);
    handleChange(value);
    toast.success(`Set value to "${value}"`);
  }, [component.min, component.max, handleChange]);

  const handleOptionDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleOptionDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleTextDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTextDragOver(true);
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleTextDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsTextDragOver(false);
    }
  };

  // Render inline text library for non-image components
  const renderTextLibrary = () => {
    if (component.type === "image") return null;

    return (
      <div className="bg-gray-50 rounded-lg border border-gray-200 px-2 pb-1">
        {/* Compact category tabs at top */}
        <div className="overflow-x-auto hide-scrollbar">
          <Tabs
            size="small"
            activeKey={selectedCategory}
            onChange={setSelectedCategory}
            tabBarGutter={4}
            className="mb-1 compact-tabs"
            items={categories.map((cat) => ({
              key: cat.value,
              label: <span className="text-[11px]">{cat.label}</span>,
            }))}
          />
        </div>

        {/* Horizontal scrollable text list with click functionality */}
        <div
          className="flex gap-1 overflow-x-auto hide-scrollbar pb-1 pt-0 scroll-smooth snap-x snap-mandatory"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {filteredTexts.slice(0, 20).map((text, index) => (
            <div
              key={`${text}-${index}`}
              onClick={() => handleTextClick(text)}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-full shadow-sm border border-gray-200 text-[11px] snap-start cursor-pointer min-w-max hover:bg-blue-100 hover:border-blue-300 transition-colors active:bg-blue-200"
              title={`Click to ${['text', 'textarea', 'textbox', 'character varying'].includes(component.type) ? 'append' : 'add'} "${text}"`}
            >
              <PlusOutlined className="text-blue-500 text-[10px]" />
              <span className="whitespace-nowrap">{text}</span>
            </div>
          ))}
        </div>

        {filteredTexts.length === 0 && (
          <div className="text-center py-1">
            <Text type="secondary" className="text-xs">
              No texts available
            </Text>
          </div>
        )}

        {/* Helper text */}
        <div className="text-center py-1">
          <Text type="secondary" className="text-[10px]">
            {['radio', 'radiobutton', 'dropdown', 'checkbox'].includes(component.type)
              ? ''
              : ''
            }
          </Text>
        </div>
      </div>
    );
  };

  const renderComponent = () => {
    switch (component.type) {
      case "image":
        return (
          <ImageDropZone
            stepIndex={stepIndex}
            unitIndex={unitIndex}
            componentName={component.name}
            currentImage={currentValue}
            placeholderImage={component.placeholderimage}
            isCommon={isCommon}
            required={component.required}
            onImageChange={onImageChange}
            onRemoveImage={onRemoveImage}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            dragOverTarget={dragOverTarget}
            moduleKey={stepname}
          />
        );

      case "radio":
      case "radiobutton":
        return (
          <Controller
            name={fieldName}
            control={control}
            rules={{ required: component.required ? `${component.name} is required` : false }}
            render={({ field, fieldState }) => (
              <div className="w-full">
                <Radio.Group
                  {...field}
                  value={currentValue}
                  onChange={(e) => {
                    field.onChange(e);
                    handleChange(e.target.value);
                  }}
                  onFocus={() => setActiveFieldRef(field)}
                  className="w-full"
                >
                  <Space direction="vertical" className="w-full">
                    {component.options?.map((option, index) => (
                      <Radio
                        key={typeof option === 'string' ? option : option.value || index}
                        value={typeof option === 'string' ? option : option.value}
                        className="mobile-radio"
                      >
                        <span className="text-sm">
                          {typeof option === 'string' ? option : option.label}
                        </span>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
                {fieldState.error && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldState.error.message}
                  </div>
                )}
              </div>
            )}
          />
        );

      case "checkbox":
        return (
          <Controller
            name={fieldName}
            control={control}
            rules={{
              validate: (value) =>
                component.required && (!value || value.length === 0)
                  ? `${component.name} is required`
                  : true,
            }}
            render={({ field, fieldState }) => (
              <div className="w-full">
                <Checkbox.Group
                  {...field}
                  value={currentValue || []}
                  onChange={(checkedValues) => {
                    field.onChange(checkedValues);
                    handleChange(checkedValues);
                  }}
                  onFocus={() => setActiveFieldRef(field)}
                  className="w-full"
                >
                  <Space direction="vertical" className="w-full">
                    {component.options?.map((option, index) => (
                      <Checkbox
                        key={typeof option === 'string' ? option : option.value || index}
                        value={typeof option === 'string' ? option : option.value}
                        className="mobile-checkbox"
                      >
                        <span className="text-sm">
                          {typeof option === 'string' ? option : option.label}
                        </span>
                      </Checkbox>
                    ))}
                  </Space>
                </Checkbox.Group>
                {fieldState.error && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldState.error.message}
                  </div>
                )}
              </div>
            )}
          />
        );

      case "dropdown":
        return (
          <Controller
            name={fieldName}
            control={control}
            rules={{ required: component.required ? `${component.name} is required` : false }}
            render={({ field, fieldState }) => (
              <div className="w-full">
                <Select
                  {...field}
                  value={currentValue}
                  style={{ width: "100%" }}
                  placeholder={`Select ${(component.name).toLowerCase()}`}
                  onChange={(value) => {
                    field.onChange(value);
                    handleChange(value);
                  }}
                  onFocus={() => setActiveFieldRef(field)}
                  size="middle"
                  className="mobile-select"
                >
                  {component.options?.map((option, index) => (
                    <Option
                      key={typeof option === 'string' ? option : option.value || index}
                      value={typeof option === 'string' ? option : option.value}
                    >
                      {typeof option === 'string' ? option : option.label}
                    </Option>
                  ))}
                </Select>
                {fieldState.error && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldState.error.message}
                  </div>
                )}
              </div>
            )}
          />
        );

      case "number":
      case "integer":
      case "numeric":
        return (
          <Controller
            name={fieldName}
            control={control}
            rules={{
              required: component.required ? `${component.name} is required` : false,
              min: component.min !== undefined ? { value: component.min, message: `Minimum value is ${component.min}` } : undefined,
              max: component.max !== undefined ? { value: component.max, message: `Maximum value is ${component.max}` } : undefined,
            }}
            render={({ field, fieldState }) => (
              <div className="w-full">
                <InputNumber
                  {...field}
                  value={currentValue}
                  style={{ width: "100%" }}
                  min={component.min}
                  max={component.max}
                  placeholder={component.placeholder || `Enter ${(component.name).toLowerCase()}`}
                  onChange={(value) => {
                    field.onChange(value);
                    handleChange(value);
                  }}
                  onFocus={() => setActiveFieldRef(field)}
                  size="middle"
                  className="mobile-input-number"
                />
                {fieldState.error && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldState.error.message}
                  </div>
                )}
              </div>
            )}
          />
        );

      case "textarea":
      case "character varying":
      case "textbox":
      case "text":
        return (
          <Controller
            name={fieldName}
            control={control}
            rules={{ required: component.required ? `${component.name} is required` : false }}
            render={({ field, fieldState }) => (
              <div className="w-full">
                <SpeechEnabledInput
                  type={component.type}
                  value={currentValue}
                  onChange={(e) => {
                    field.onChange(e);
                    handleChange(e.target.value);
                  }}
                  onFocus={() => setActiveFieldRef(field)}
                  placeholder={component.placeholder || `Enter ${(component.name).toLowerCase()}`}
                  rows={component.type === "textarea" ? 4 : undefined}
                  size="middle"
                  className={component.type === "textarea" ? "mobile-textarea" : "mobile-input"}
                />
                {fieldState.error && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldState.error.message}
                  </div>
                )}
              </div>
            )}
          />
        );

      default:
        return (
          <div className="p-4 bg-gray-100 rounded border-dashed border-2 border-gray-300">
            <Text type="secondary" className="text-sm">
              Unsupported component type: {component.type}
            </Text>
          </div>
        );
    }
  };

  // For image components, return directly without label wrapper and text library
  if (component.type === "image") {
    return renderComponent();
  }

  // For other components, show label and text library
  return (
    <div className="w-full" style={{ marginBottom: hideLabel ? "0" : "16px" }}>
      {!hideLabel && (
        <Text strong style={{ display: "block", marginBottom: "8px" }}>
          {component.name}
          {component.unit && ` (${component.unit})`}
          {component.required && <span style={{ color: "red" }}>*</span>}
        </Text>
      )}
      {renderComponent()}
      {renderTextLibrary()}

      <style jsx>{`
        .text-library-scroll {
          max-height: 60px;
          overflow: hidden;
        }
        
        .text-library-scroll::-webkit-scrollbar {
          height: 4px;
        }
        
        .text-library-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 2px;
        }
        
        .text-library-scroll::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 2px;
        }
        
        .inline-text-item {
          display: flex;
          align-items: center;
          padding: 4px 8px;
          background: white;
          border: 1px solid #d9d9d9;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 11px;
          white-space: nowrap;
          min-width: fit-content;
          user-select: none;
        }
        
        .inline-text-item:hover {
          background: #e6f4ff;
          border-color: #40a9ff;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .inline-text-item:active {
          background: #bae7ff;
          transform: translateY(0);
        }
        
        .drag-icon-small {
          margin-right: 4px;
          font-size: 10px;
          color: #1890ff;
          opacity: 0.8;
        }
        
        .inline-text-item:hover .drag-icon-small {
          opacity: 1;
          color: #096dd9;
        }

        @media (max-width: 768px) {
          .mobile-radio .ant-radio-wrapper {
            padding: 8px 0;
          }
          
          .mobile-checkbox .ant-checkbox-wrapper {
            padding: 8px 0;
          }
          
          .mobile-select .ant-select-selector {
            min-height: 40px;
          }
          
          .mobile-input-number input {
            height: 40px;
          }
          
          .mobile-input input {
            height: 40px;
          }
          
          .mobile-textarea textarea {
            min-height: 80px;
          }
          
          .ant-radio, .ant-checkbox {
            transform: scale(1.2);
            margin-right: 12px;
          }
          
          .ant-select-arrow {
            font-size: 16px;
          }
          
          .ant-space-vertical {
            width: 100%;
          }
          
          .ant-space-vertical > .ant-space-item {
            margin-bottom: 8px !important;
          }
          
          .inline-text-item {
            padding: 3px 6px;
            font-size: 10px;
          }
          
          .drag-icon-small {
            font-size: 8px;
            margin-right: 2px;
          }
        }
        
        @media (min-width: 769px) {
          .ant-radio, .ant-checkbox {
            margin-right: 8px;
          }
        }
        
        .ant-input:focus,
        .ant-input-number:focus,
        .ant-select:focus .ant-select-selector,
        .ant-radio-wrapper:focus-within,
        .ant-checkbox-wrapper:focus-within {
          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
          border-color: #40a9ff;
          outline: none;
        }
        
        .drag-over-option {
          background-color: #f0f8ff !important;
          border: 2px dashed #1890ff !important;
          border-radius: 6px;
          padding: 8px;
          transition: all 0.2s ease;
        }
        
        .drag-over-text {
          background-color: #f0fff0 !important;
          border: 2px dashed #52c41a !important;
          border-radius: 6px;
          padding: 8px;
          transition: all 0.2s ease;
        }
      `}</style>
    </div>
  );
};

export default DynamicFormComponent;